import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight } from "@phosphor-icons/react";
import { chairPrompts, tableCopy } from "../../content";
import { useGame } from "../../game/GameContext";
import { Waypoint } from "../game/Waypoint";

const EASE = [0.16, 1, 0.3, 1] as const;

export function NoPitches() {
  const reduce = useReducedMotion();
  const { stamp } = useGame();
  const [active, setActive] = useState(0);

  return (
    <section id="table" className="overflow-hidden bg-paper-deep py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <Waypoint
          station="table"
          title={tableCopy.titleLines.map((line) => <span key={line} className="block">{line}</span>)}
          sub={tableCopy.sub}
        />

        <div className="mt-14 grid items-stretch gap-8 lg:grid-cols-[1.35fr_0.65fr] lg:gap-12">
          <motion.figure
            initial={reduce ? false : { opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="group relative min-h-[25rem] overflow-hidden rounded-md border-2 border-ink bg-night shadow-lift md:min-h-[35rem]"
          >
            <motion.img
              src="/page-assets/community-table.jpg"
              alt="Builders and friendly harbour otters share unfinished work around a dockside table as boats gather beneath a lit lighthouse."
              className="absolute inset-0 h-full w-full object-cover"
              whileInView={reduce ? undefined : { scale: [1.04, 1] }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.6, ease: EASE }}
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(17,30,43,0.82))]" />
            <figcaption className="absolute right-6 bottom-6 left-6 text-foam md:right-8 md:bottom-8 md:left-8">
              <p className="font-mono text-[10px] tracking-[0.16em] text-signal uppercase">The table rule</p>
              <p className="mt-2 max-w-[32ch] text-xl leading-snug font-semibold md:text-2xl">Bring the unfinished thing. Leave the pitch aboard.</p>
            </figcaption>
          </motion.figure>

          <motion.aside
            initial={reduce ? false : { opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.75, delay: 0.12, ease: EASE }}
            className="flex flex-col justify-between border-t-2 border-ink pt-6 lg:border-t-0 lg:border-l-2 lg:pt-0 lg:pl-9"
          >
            <div>
              <p className="font-mono text-[10px] tracking-[0.16em] text-muted uppercase">Pull up a chair</p>
              <div className="mt-5 space-y-1">
                {chairPrompts.map((prompt, index) => (
                  <button
                    key={prompt}
                    type="button"
                    aria-pressed={active === index}
                    onClick={() => {
                      setActive(index);
                      stamp("table");
                    }}
                    className={`group flex w-full items-center gap-3 border-b border-line py-3 text-left text-sm transition-colors ${active === index ? "text-ink" : "text-muted hover:text-ink"}`}
                  >
                    <span className={`font-mono text-[10px] ${active === index ? "text-signal-deep" : "text-muted"}`}>{String(index + 1).padStart(2, "0")}</span>
                    <span className="flex-1">{prompt}</span>
                    <ArrowRight className={`size-4 transition-transform ${active === index ? "translate-x-0 text-signal-deep" : "-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"}`} />
                  </button>
                ))}
              </div>
            </div>

            <div aria-live="polite" className="mt-10 min-h-32 border border-ink bg-foam p-6 shadow-card">
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={active}
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, y: -8 }}
                  transition={{ duration: 0.24, ease: EASE }}
                  className="font-hand text-2xl leading-tight text-ink md:text-3xl"
                >
                  {chairPrompts[active]}
                </motion.p>
              </AnimatePresence>
              <p className="mt-4 font-mono text-[10px] text-muted">{tableCopy.note}</p>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
